import * as React from 'react';
import styled from 'styled-components';

import UsersForm from './UsersForm';
import { Loader } from '../atoms';
import { InputTextWithLabel, ModalDefaultDraggable } from '../molecules';
import {
  searchCompaniesByEmailDomain,
  getPermitUsers,
  createUser,
  CompanyTableRow,
  UserType,
  UserTableType,
} from '../api/users';
import {
  CLIENT_TYPES_VALUES,
  USER_TYPES_VALUES,
} from '../constants/forms';
import { FormServerError } from '../types';

type Step = 'choose' | 'searchEmail' | 'form';
type SearchMode = 'email' | 'company';

// Opens the wizard straight on the form with these values (e.g. a Monday
// lead's requester) instead of asking "associated with a company?".
export interface ContactPrefill {
  startWith: 'company' | 'individual';
  company?: Partial<UserTableType>;
  individual?: Partial<UserTableType>;
  // Parent company for the individual, when it already exists in EPIC.
  parentCompany?: CompanyTableRow | null;
}

interface Props {
  onSubmit: (data: UserType) => void;
  modalError: boolean;
  initialErrors?: FormServerError<UserType>;
  setModalTitle?: (title: string) => void;
  prefill?: ContactPrefill;
  // Company saved through "Save & Add Individual", before the individual.
  onCompanyCreated?: (company: CompanyTableRow) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// A bare domain like "example.com" — no "@", at least one dot.
const DOMAIN_REGEX = /^[^\s@]+\.[^\s@]+$/;

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.ca', 'ymail.com', 'rocketmail.com',
  'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
  'aol.com', 'aim.com',
  'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me', 'pm.me',
  'yandex.com', 'yandex.ru',
  'mail.com', 'gmx.com', 'gmx.net', 'gmx.us',
  'zoho.com', 'fastmail.com', 'tutanota.com', 'tuta.io', 'hey.com',
  'comcast.net', 'verizon.net', 'sbcglobal.net', 'att.net',
  'qq.com', '163.com', '126.com',
]);

// Accepts either a full email ("user@example.com") or a bare domain
// ("example.com") and returns the lowercased domain part.
const extractDomain = (input: string) => {
  const at = input.lastIndexOf('@');
  return (at >= 0 ? input.slice(at + 1) : input).trim().toLowerCase();
};

// Valid lookup input is either a full email or a bare domain.
const isSearchableInput = (input: string) =>
  EMAIL_REGEX.test(input) || DOMAIN_REGEX.test(input);

const isFreeEmailDomain = (input: string) =>
  FREE_EMAIL_DOMAINS.has(extractDomain(input));

// Legal-entity suffixes that don't make two company names different
// ("Boyette Construction LLC" vs "Boyette Construction, Inc.").
const COMPANY_SUFFIXES = new Set([
  'llc', 'inc', 'incorporated', 'corp', 'corporation', 'co', 'company',
  'ltd', 'limited', 'lp', 'llp', 'pllc', 'pa', 'pc',
]);

const normalizeCompanyName = (name?: unknown) => {
  const words = String(name || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  while (words.length > 1 && COMPANY_SUFFIXES.has(words[words.length - 1])) {
    words.pop();
  }
  return words.join(' ');
};

// Street line only ("5741 Washington Street"), so "…, Naples, FL 34109, USA"
// and "…, Naples, FL, USA" compare equal.
const normalizeStreet = (address?: unknown) =>
  String(address || '')
    .split(',')[0]
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const streetKeys = (...addresses: unknown[]) =>
  addresses.map(normalizeStreet).filter(k => k.length >= 6);

const isLikelySameCompany = (
  candidate: CompanyTableRow,
  name: string,
  streets: string[],
) => {
  const other = normalizeCompanyName(candidate.companyName);
  if (other && name) {
    if (other === name) return true;
    // One name contained in the other, on word boundaries
    // ("Boyette Construction" vs "Boyette Construction Group").
    const [shorter, longer] =
      other.length < name.length ? [other, name] : [name, other];
    if (
      shorter.length >= 4 &&
      ` ${longer} `.includes(` ${shorter} `)
    ) {
      return true;
    }
  }
  const otherStreets = streetKeys(candidate.street, candidate.address);
  return streets.some(k => otherStreets.includes(k));
};

// Looks up existing companies that look like the one about to be created.
// The API search is a plain substring match, so the full name alone would
// miss "Group" / "LLC" variants — also search on the first distinctive word
// (usually the brand, e.g. "Boyette"), then narrow client-side.
export const findSimilarCompanies = async (
  companyName: string,
  addresses: unknown[] = [],
): Promise<CompanyTableRow[]> => {
  const name = normalizeCompanyName(companyName);
  if (!name) return [];
  const firstWord = name
    .split(' ')
    .find(w => w.length >= 3 && w !== 'the' && w !== 'and');
  // Also search by street line so a same-address company under a different
  // name ("BCG Builders" at Boyette's address) comes back from the API.
  const streets = streetKeys(...addresses);
  const terms = Array.from(
    new Set([companyName.trim(), firstWord, ...streets].filter(Boolean)),
  ) as string[];
  // One failed search shouldn't discard the other's matches.
  const responses: any[] = await Promise.all(
    terms.map(search =>
      getPermitUsers({
        search,
        limit: 50,
        offset: 0,
        userRole: USER_TYPES_VALUES.CLIENT,
        clientType: CLIENT_TYPES_VALUES.COMPANY,
      } as any).catch(e => {
        console.warn('[duplicate-company] search failed', search, e);
        return null;
      })),
  );
  const byId = new Map<number, CompanyTableRow>();
  responses.forEach(res => {
    const data = res?.data?.results || res?.data;
    const rows = (Array.isArray(data) ? data : []) as CompanyTableRow[];
    rows.forEach(c => {
      if (!byId.has(c.id) && isLikelySameCompany(c, name, streets)) {
        byId.set(c.id, c);
      }
    });
  });
  return Array.from(byId.values());
};

const AddContactWizard = ({
  onSubmit,
  modalError,
  initialErrors,
  setModalTitle,
  prefill,
  onCompanyCreated,
}: Props) => {
  const [step, setStep] = React.useState<Step>(prefill ? 'form' : 'choose');
  const [
    individualPrevStep,
    setIndividualPrevStep,
  ] = React.useState<'choose' | 'searchEmail'>('choose');
  const [individualEmail, setIndividualEmail] = React.useState('');
  const [emailDomain, setEmailDomain] = React.useState('');
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchPerformed, setSearchPerformed] = React.useState(false);
  const [
    searchResults,
    setSearchResults,
  ] = React.useState<CompanyTableRow[]>([]);
  const [
    selectedCompany,
    setSelectedCompany,
  ] = React.useState<CompanyTableRow | null>(prefill?.parentCompany || null);
  const [companyMode, setCompanyMode] = React.useState(prefill?.startWith === 'company');
  const [
    localErrors,
    setLocalErrors,
  ] = React.useState<FormServerError<UserType> | null>(null);

  const [searchMode, setSearchMode] = React.useState<SearchMode>('email');
  const [companyQuery, setCompanyQuery] = React.useState('');
  const [companyLoading, setCompanyLoading] = React.useState(false);
  const [companySearched, setCompanySearched] = React.useState(false);
  const [
    companyResults,
    setCompanyResults,
  ] = React.useState<CompanyTableRow[]>([]);
  const companySearchTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const companyLastQueryRef = React.useRef<string>('');

  const [
    duplicateCompanies,
    setDuplicateCompanies,
  ] = React.useState<CompanyTableRow[] | null>(null);
  const duplicateResolveRef = React.useRef<((proceed: boolean) => void) | null
  >(null);

  React.useEffect(() => {
    if (step === 'form' && setModalTitle) {
      setModalTitle(companyMode ? 'Add Company' : 'Add Individual');
    }
  }, [step, companyMode]);

  React.useEffect(
    () => () => {
      if (companySearchTimeoutRef.current) {
        clearTimeout(companySearchTimeoutRef.current);
      }
    },
    [],
  );

  const handleNoCompany = () => {
    setCompanyMode(false);
    setSelectedCompany(null);
    setIndividualEmail('');
    setIndividualPrevStep('choose');
    setStep('form');
  };

  const handleYesCompany = () => {
    setStep('searchEmail');
  };

  const handleCreateCompany = () => {
    setCompanyMode(true);
    setSelectedCompany(null);
    setLocalErrors(null);
    setStep('form');
  };

  const handleSaveCompanyAndAddIndividual = async (
    data: UserType,
  ) => {
    setLocalErrors(null);
    try {
      const res = await createUser(data);
      const newCompany = res.data;
      const company = {
        id: newCompany.id,
        companyName: newCompany.companyName,
        email: newCompany.email,
        phoneNumber: newCompany.phoneNumber,
        address: newCompany.address,
        street: newCompany.street,
        city: newCompany.city,
        state: newCompany.state,
        zipCode: newCompany.zipCode,
        isActive: newCompany.isActive,
        clientType: 2,
        userRole: 3,
      } as CompanyTableRow;
      setSelectedCompany(company);
      if (onCompanyCreated) onCompanyCreated(company);
      setIndividualPrevStep('searchEmail');
      setCompanyMode(false);
    } catch (err) {
      const response = (err as any)?.response;
      if (response?.data) {
        setLocalErrors(response.data);
      }
    }
  };

  // UsersForm `beforeSubmit` in company mode: if similar companies already
  // exist, hold the submit open until the user picks one or confirms.
  const confirmNotDuplicateCompany = async (
    values: UserTableType,
  ): Promise<boolean> => {
    const name = String((values as any)?.companyName || '').trim();
    if (!name) {
      console.warn('[duplicate-company] no company name in form values', values);
      return true;
    }
    let matches: CompanyTableRow[] = [];
    try {
      matches = await findSimilarCompanies(name, [
        (values as any)?.street,
        (values as any)?.address,
      ]);
    } catch (e) {
      // A failed lookup shouldn't block creating the company.
      console.warn('[duplicate-company] lookup failed', e);
      return true;
    }
    console.info('[duplicate-company]', name, '→', matches.length, 'match(es)');
    if (!matches.length) return true;
    return new Promise<boolean>(resolve => {
      duplicateResolveRef.current = resolve;
      setDuplicateCompanies(matches);
    });
  };

  const closeDuplicatePrompt = (proceed: boolean) => {
    const resolve = duplicateResolveRef.current;
    duplicateResolveRef.current = null;
    setDuplicateCompanies(null);
    if (resolve) resolve(proceed);
  };

  const performSearch = async () => {
    const trimmed = individualEmail.trim();
    if (!isSearchableInput(trimmed)) return;

    const domain = extractDomain(trimmed);

    setEmailDomain(domain);
    setSearchLoading(true);
    setSearchPerformed(false);

    try {
      const res = await searchCompaniesByEmailDomain(domain);
      const rows =
        (res.data && (res.data as any).results) || res.data || [];
      const matches = (rows as CompanyTableRow[]).filter(
        c =>
          typeof c.email === 'string' &&
          c.email.toLowerCase().includes(domain),
      );
      setSearchResults(matches);
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
      setSearchPerformed(true);
    }
  };

  const handleCompanyQueryChange = (value: string) => {
    setCompanyQuery(value);
    if (companySearchTimeoutRef.current) {
      clearTimeout(companySearchTimeoutRef.current);
    }
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setCompanyResults([]);
      setCompanySearched(false);
      setCompanyLoading(false);
      return;
    }
    setCompanyLoading(true);
    companySearchTimeoutRef.current = setTimeout(() => {
      companyLastQueryRef.current = trimmed;
      getPermitUsers({
        search: trimmed,
        limit: 25,
        offset: 0,
        userRole: USER_TYPES_VALUES.CLIENT,
        clientType: CLIENT_TYPES_VALUES.COMPANY,
      } as any)
        .then((res: any) => {
          if (companyLastQueryRef.current !== trimmed) return;
          const rows = (res?.data?.results ||
            res?.data ||
            []) as CompanyTableRow[];
          setCompanyResults(rows);
          setCompanySearched(true);
        })
        .catch(() => {
          if (companyLastQueryRef.current !== trimmed) return;
          setCompanyResults([]);
          setCompanySearched(true);
        })
        .finally(() => {
          if (companyLastQueryRef.current === trimmed) {
            setCompanyLoading(false);
          }
        });
    }, 300);
  };

  const handleSelectCompany = (company: CompanyTableRow) => {
    setCompanyMode(false);
    setSelectedCompany(company);
    setIndividualPrevStep('searchEmail');
    setStep('form');
  };

  const handleSkipCompany = () => {
    setCompanyMode(false);
    setSelectedCompany(null);
    setIndividualPrevStep('searchEmail');
    setStep('form');
  };

  if (step === 'choose') {
    return (
      <Container>
        <Heading>Is this individual associated with a company?</Heading>
        <Helper>
          Choose how to set up the contact. You can change these
          details on the next step.
        </Helper>
        <ChoiceRow>
          <ChoiceCard onClick={handleNoCompany} type="button">
            <ChoiceTitle>No</ChoiceTitle>
            <ChoiceSub>
              Add an individual with no parent company.
            </ChoiceSub>
          </ChoiceCard>
          <ChoiceCard $primary onClick={handleYesCompany} type="button">
            <ChoiceTitle>Yes</ChoiceTitle>
            <ChoiceSub>
              Look up an existing parent company by email domain.
            </ChoiceSub>
          </ChoiceCard>
        </ChoiceRow>
      </Container>
    );
  }

  if (step === 'searchEmail') {
    const trimmedEmail = individualEmail.trim();
    const inputValid = isSearchableInput(trimmedEmail);
    const freeDomain = inputValid && isFreeEmailDomain(trimmedEmail);
    const searchable = inputValid && !freeDomain;
    const enteredDomain = extractDomain(trimmedEmail);

    const trimmedCompanyQuery = companyQuery.trim();
    const companyQueryReady = trimmedCompanyQuery.length >= 3;

    const renderResultRow = (c: CompanyTableRow) => {
      const addressBits = [c.address, c.city, c.state]
        .filter(Boolean)
        .join(', ');
      return (
        <ResultRow key={c.id}>
          <ResultInfo>
            <ResultTitle>{c.companyName || '—'}</ResultTitle>
            {c.email && <ResultSub>{c.email}</ResultSub>}
            {addressBits && <ResultSub>{addressBits}</ResultSub>}
          </ResultInfo>
          <PickButton
            type="button"
            onClick={() => handleSelectCompany(c)}
          >
            Select
          </PickButton>
        </ResultRow>
      );
    };

    return (
      <Container>
        <Heading>Find the parent company</Heading>
        <Helper>
          {searchMode === 'email'
            ? "Enter the new user's email or a company domain. We'll match the domain against existing companies on file."
            : 'Type at least 3 characters to search existing companies by name.'}
        </Helper>

        <ModeTabs role="tablist">
          <ModeTab
            type="button"
            role="tab"
            aria-selected={searchMode === 'email'}
            $active={searchMode === 'email'}
            onClick={() => setSearchMode('email')}
          >
            Search by Email/Domain
          </ModeTab>
          <ModeTab
            type="button"
            role="tab"
            aria-selected={searchMode === 'company'}
            $active={searchMode === 'company'}
            onClick={() => setSearchMode('company')}
          >
            Search by Company
          </ModeTab>
        </ModeTabs>

        {searchMode === 'email' && (
          <>
            <SearchRow>
              <SearchInputWrap>
                <InputTextWithLabel
                  name="individualEmail"
                  label="User email or domain"
                  placeholder="user@example.com or example.com"
                  value={individualEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIndividualEmail(e.target.value)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (searchable) performSearch();
                    }
                  }}
                />
              </SearchInputWrap>
              <SearchButton
                type="button"
                disabled={!searchable || searchLoading}
                onClick={performSearch}
              >
                Search
              </SearchButton>
            </SearchRow>

            {freeDomain && (
              <DomainWarning>
                <DomainWarningTitle>
                  @{enteredDomain} is a personal email provider
                </DomainWarningTitle>
                <DomainWarningBody>
                  Parent-company lookup only works with custom company
                  domains (for example, <em>@yourcompany.com</em>). If
                  this person doesn&apos;t have a work email, choose
                  <strong> &quot;Continue without a company&quot;</strong>{' '}
                  below to add them as an individual.
                </DomainWarningBody>
              </DomainWarning>
            )}

            {searchLoading && (
              <LoaderWrap>
                <Loader size="36px" />
              </LoaderWrap>
            )}

            {!searchLoading && searchPerformed && (
              <Results>
                <ResultsHeading>
                  {searchResults.length > 0
                    ? `Companies matching "${emailDomain}"`
                    : `No companies found for "${emailDomain}"`}
                </ResultsHeading>
                {searchResults.map(renderResultRow)}

                <CreateNewPrompt $emphasized={searchResults.length === 0}>
                  <CreateNewText>
                    {searchResults.length > 0
                      ? "Don't see the company you're looking for?"
                      : 'Want to add this company to the system?'}
                  </CreateNewText>
                  <CreateNewLink type="button" onClick={handleCreateCompany}>
                    Create new company
                  </CreateNewLink>
                </CreateNewPrompt>
              </Results>
            )}
          </>
        )}

        {searchMode === 'company' && (
          <>
            <SearchRow>
              <SearchInputWrap>
                <InputTextWithLabel
                  name="companyQuery"
                  label="Company name"
                  placeholder="Type at least 3 characters"
                  value={companyQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCompanyQueryChange(e.target.value)
                  }
                />
              </SearchInputWrap>
            </SearchRow>

            {companyLoading && (
              <LoaderWrap>
                <Loader size="36px" />
              </LoaderWrap>
            )}

            {!companyLoading && companyQueryReady && companySearched && (
              <Results>
                <ResultsHeading>
                  {companyResults.length > 0
                    ? `Companies matching "${trimmedCompanyQuery}"`
                    : `No companies found for "${trimmedCompanyQuery}"`}
                </ResultsHeading>
                {companyResults.map(renderResultRow)}

                <CreateNewPrompt $emphasized={companyResults.length === 0}>
                  <CreateNewText>
                    {companyResults.length > 0
                      ? "Don't see the company you're looking for?"
                      : 'Want to add this company to the system?'}
                  </CreateNewText>
                  <CreateNewLink type="button" onClick={handleCreateCompany}>
                    Create new company
                  </CreateNewLink>
                </CreateNewPrompt>
              </Results>
            )}
          </>
        )}

        <FooterRow>
          <SecondaryBtn type="button" onClick={() => setStep('choose')}>
            Back
          </SecondaryBtn>
          <SecondaryBtn type="button" onClick={handleSkipCompany}>
            Continue without a company
          </SecondaryBtn>
        </FooterRow>
      </Container>
    );
  }

  // An individual under a selected company starts with the company's address.
  const companyAddress =
    !companyMode && selectedCompany
      ? {
          address: selectedCompany.address || '',
          street: selectedCompany.street || '',
          city: selectedCompany.city || '',
          state: selectedCompany.state || '',
          zipCode: selectedCompany.zipCode || '',
        }
      : {};

  const initialValues = ({
    ...companyAddress,
    userRole: USER_TYPES_VALUES.CLIENT,
    clientType: companyMode
      ? CLIENT_TYPES_VALUES.COMPANY
      : CLIENT_TYPES_VALUES.INDIVIDUAL,
    parentCompany: companyMode
      ? undefined
      : selectedCompany || undefined,
    email:
      companyMode || !EMAIL_REGEX.test(individualEmail.trim())
        ? ''
        : individualEmail,
    isActive: true,
    ...(companyMode ? prefill?.company : prefill?.individual),
  } as unknown) as UserTableType;

  return (
    <>
      <UsersForm
        key={companyMode ? 'company' : 'individual'}
        beforeSubmit={companyMode ? confirmNotDuplicateCompany : undefined}
        disabledRole
        lockClientType
        companyMode={companyMode}
        onBack={
        companyMode
          ? () => setStep('searchEmail')
          : () => setStep(individualPrevStep)
      }
        updateButtonText={companyMode ? 'Save & Add Individual' : 'Save'}
        initialValues={initialValues}
        modalError={modalError}
        initialErrors={
        companyMode && localErrors ? localErrors : initialErrors
      }
        onSubmit={
        companyMode
          ? (handleSaveCompanyAndAddIndividual as (
              data: UserType,
            ) => void)
          : onSubmit
      }
        secondarySubmitButton={
        companyMode
          ? {
              label: 'Save',
              onClick: onSubmit,
            }
          : undefined
      }
        submitHelperText={
        companyMode
          ? "Choose 'Save' to finish, or 'Save & Add Individual' to immediately add a contact under this company."
          : undefined
      }
      />
      {duplicateCompanies && (
      <ModalDefaultDraggable
        title="Possible duplicate company"
        onClose={() => closeDuplicatePrompt(false)}
        onClickAwayClose={false}
        centered
        zIndex={30}
      >
        <Container>
          <DomainWarning>
            <DomainWarningTitle>
              {duplicateCompanies.length === 1
                ? 'A similar company already exists'
                : `${duplicateCompanies.length} similar companies already exist`}
            </DomainWarningTitle>
            <DomainWarningBody>
              To avoid duplicates, select the existing company if it&apos;s
              the same one. Only create a new company if it&apos;s a
              different business.
            </DomainWarningBody>
          </DomainWarning>
          <DuplicateList>
            {duplicateCompanies.map(c => {
              const addressBits = [c.address, c.city, c.state]
                .filter(Boolean)
                .join(', ');
              return (
                <ResultRow key={c.id}>
                  <ResultInfo>
                    <ResultTitle>{c.companyName || '—'}</ResultTitle>
                    {c.email && <ResultSub>{c.email}</ResultSub>}
                    {addressBits && <ResultSub>{addressBits}</ResultSub>}
                  </ResultInfo>
                  <PickButton
                    type="button"
                    onClick={() => {
                      closeDuplicatePrompt(false);
                      handleSelectCompany(c);
                    }}
                  >
                    Use this company
                  </PickButton>
                </ResultRow>
              );
            })}
          </DuplicateList>
          <FooterRow>
            <SecondaryBtn
              type="button"
              onClick={() => closeDuplicatePrompt(false)}
            >
              Cancel
            </SecondaryBtn>
            <SecondaryBtn
              type="button"
              onClick={() => closeDuplicatePrompt(true)}
            >
              Create new company anyway
            </SecondaryBtn>
          </FooterRow>
        </Container>
      </ModalDefaultDraggable>
    )}
    </>
  );
};

const DuplicateList = styled.div`
  max-height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Container = styled.div`
  width: 100%;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
  text-align: left;
`;

const Heading = styled.h2`
  margin: 0 0 8px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const Helper = styled.p`
  margin: 0 0 24px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const ChoiceRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const ChoiceCard = styled.button<{ $primary?: boolean }>`
  text-align: left;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid
    ${({ $primary }) =>
      $primary ? 'var(--primary-600)' : 'var(--border)'};
  background: ${({ $primary }) =>
    $primary ? 'var(--primary-50)' : 'var(--neutral-0)'};
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;
  font-family: inherit;

  &:hover {
    border-color: var(--primary-600);
    background: ${({ $primary }) =>
      $primary ? 'var(--primary-100)' : 'var(--neutral-50)'};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ChoiceTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 6px;
`;

const ChoiceSub = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.4;
`;

const ModeTabs = styled.div`
  display: flex;
  gap: 4px;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 4px;
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const ModeTab = styled.button<{ $active?: boolean }>`
  flex: 1;
  height: 40px;
  border-radius: 8px;
  border: none;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.9375rem;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  background: ${({ $active }) =>
    $active ? 'var(--primary-600)' : 'transparent'};
  color: ${({ $active }) =>
    $active ? 'var(--neutral-0)' : 'var(--neutral-600)'};

  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--primary-700)' : 'var(--neutral-200)'};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SearchRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInputWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const SearchButton = styled.button`
  height: 40px;
  min-width: 120px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-400);
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const DomainWarning = styled.div`
  margin: 4px 0 16px;
  padding: 12px 14px;
  border: 1px solid var(--danger-100, #fecaca);
  background: var(--danger-50, #fef2f2);
  border-radius: 8px;
  color: var(--danger-700, #b91c1c);
`;

const DomainWarningTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 4px;
`;

const DomainWarningBody = styled.div`
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--neutral-700);

  em {
    font-style: normal;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.8125em;
    background: var(--neutral-100);
    padding: 1px 6px;
    border-radius: 4px;
  }

  strong {
    color: var(--mainText);
  }
`;

const Results = styled.div`
  margin-bottom: 16px;
`;

const ResultsHeading = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 10px;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  margin-bottom: 8px;
`;

const ResultInfo = styled.div`
  min-width: 0;
`;

const ResultTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
`;

const ResultSub = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  margin-top: 2px;
`;

const PickButton = styled.button`
  height: 32px;
  padding: 0 14px;
  border: 1px solid var(--primary-600);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--primary-700);
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-50);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const CreateNewPrompt = styled.div<{ $emphasized?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: ${({ $emphasized }) => ($emphasized ? '4px' : '12px')};
  padding: ${({ $emphasized }) => ($emphasized ? '14px 16px' : '8px 0 0')};
  border-top: ${({ $emphasized }) =>
    $emphasized ? 'none' : '1px dashed var(--border)'};
  background: ${({ $emphasized }) =>
    $emphasized ? 'var(--neutral-50)' : 'transparent'};
  border-radius: ${({ $emphasized }) => ($emphasized ? '8px' : '0')};
`;

const CreateNewText = styled.span`
  font-size: 0.8125rem;
  color: var(--neutral-600);
`;

const CreateNewLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--primary-700);
  font-weight: 600;
  font-size: 0.8125rem;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: var(--primary-800);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: 4px;
  }
`;

const SecondaryBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

export default AddContactWizard;
